import { pipe } from 'ramda';
import React, { Suspense } from 'react';
import type { DOMNode } from 'html-dom-parser';
import { attributesToProps, domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import { Link } from 'js/components/common/Link';
import { toCamelCase } from 'js/utils/string.util';
import { BackboneReplacer } from 'js/backbone/components/BackboneReplacer';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

export const parseHTMLAttributes = (attribs: {[key: string]: string} = {}): {[key: string]: string | boolean} => {
    return Object.keys(attribs).reduce((result, key) => {
        const value = attribs[key] === 'true' ? true : attribs[key] === 'false' ? false : attribs[key];

        if (key.includes('_')) {
            result[toCamelCase(key)] = value;
        } else if (key === 'class') {
            result.className = value;
        } else {
            result[key] = value;
        }

        return result;
    }, {} as {[key: string]: string | boolean});
};

export const supportOnClickAttribute = (attribs: {[name: string]: string | boolean} = {}): { [name: string]: any } => {
    const { onclick, onClick, ...rest } = attribs;

    if (!onclick && !onClick) {
        return { ...rest };
    }

    // eslint-disable-next-line no-new-func
    const fn = new Function('event', onclick as string || onClick as string);

    return { ...rest, onClick: fn };
};

export const alignAttributesWithReact = pipe(attributesToProps, supportOnClickAttribute);

export const getHTMLParserOptions = <C extends object = any, L extends object = any>(
    { componentAttributes = {} as C, linkAttributes = {} as L, components, replacers = {}, context }: {
    componentAttributes?: C;
    linkAttributes?: L;
    components: Record<string, React.ComponentType<C>>;
    replacers?: Record<string, string>;
    context?: string;
}): { options: HTMLReactParserOptions; scripts: HTMLScriptElement[] } => {
    const scripts: HTMLScriptElement[] = [];

    const options = {
        replace: (node: DOMNode): JSX.Element | undefined => {
            if (node.type === 'script') {
                const attribs = node.attribs || {};
                const script = document.createElement('script');

                if (context && (!attribs.type || attribs.type === 'text/javascript')) {
                    // in case context is provided, and this script is a regular JS script
                    // then leave a comment at the top of the script
                    script.appendChild(document.createTextNode([
                        `//# sourceURL=vm://internal/${encodeURIComponent(context)}`,
                        `/* ${context} */`,
                        domToReact(node.children as DOMNode[]).toString()
                    ].join('\n')));
                } else {
                    script.appendChild(document.createTextNode(domToReact(node.children as DOMNode[]).toString()));
                }

                for (const key of Object.keys(attribs)) {
                    script.setAttribute(key, attribs[key]);
                }

                scripts.push(script);

                // return empty react fragment so that nothing is rendered
                return <></>;
            }

            if (node.type === 'tag' && node.name === 'a') {
                // wrap all the links into Link element
                // so that regular routing can happen
                const attribs = node.attribs || {};

                return (
                    <Link
                        isDefaultLinkClass={false}
                        {...linkAttributes}
                        {...alignAttributesWithReact(attribs)}
                    >
                        {domToReact(node.children as DOMNode[], options)}
                    </Link>
                );
            }

            if (node.type === 'tag' && (node.name === 'head' || node.name === 'body')) {
                // do not support <head> and <body> dom elements, show their children instead
                return (
                    <>
                        {domToReact(node.children as DOMNode[], options)}
                    </>
                );
            }

            if (node.type === 'tag' && /^hub2-/.test(node.name)) {
                // name of the tag starts with "hub2-"
                const attribs = node.attribs || {};
                const componentName = node.name?.replace(/^hub2-|-/g, '');

                const Component = components[node.name as keyof typeof components] ||
                                  components[componentName as keyof typeof components];

                if (Component) {
                    return (
                        <Suspense fallback="">
                            {node.children.length ? (
                                <Component
                                    {...componentAttributes}
                                    {...parseHTMLAttributes(attribs)}
                                >
                                    {domToReact(node.children as DOMNode[], options)}
                                </Component>
                            ) : (
                                <Component {...componentAttributes} {...parseHTMLAttributes(attribs)} />
                            )}
                        </Suspense>
                    );
                }

                // this is here only while we have Backbone replacers
                // it should be removed once all replacers are migrated to be real React components
                if (replacers?.[componentName]) {
                    return (
                        <BackboneReplacer dynamicName={componentName} viewName={componentName} {...node.attribs}>
                            {domToReact(node.children as DOMNode[], options)}
                        </BackboneReplacer>
                    );
                }
            }

            if (node.type === 'tag') {
                const attribs = node.attribs || {};
                const props = alignAttributesWithReact(attribs);
                const childrenContent = node.children?.length ? domToReact(node.children as DOMNode[], options) : null;

                if (attribs.class?.includes('fn-redirect') && (attribs['data-href'] || attribs['data-url']) && !attribs.tabindex) {
                    attribs.tabindex = '0';
                }

                if (Number(attribs.tabindex) === 0) {
                    return (
                        <AccessibilityElement tagName={node.name} {...props}>
                            {childrenContent}
                        </AccessibilityElement>
                    );
                }

                return React.createElement(node.name, props, childrenContent);
            }
        }
    };

    return { options, scripts };
};
